import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { forexApi, type MasterTradeTrace } from "@/lib/forex-api";

function formatTime(value: unknown) {
  return typeof value === "number" ? new Date(value * 1000).toLocaleString("zh-CN", { hour12: false, timeZone: "Asia/Shanghai" }) : "--";
}

const followerSummaryLabels: Record<string, string> = {
  total: "指令总数",
  filled: "已成交",
  accepted: "已接收",
  submitted: "已提交",
  queued: "排队中",
  rejected: "已拒绝",
  failed: "失败",
  unknown: "待确认",
};

export default function MasterTradeDetailDrawer({ orderRef, onClose }: { orderRef: string; onClose: () => void }) {
  const [trace, setTrace] = useState<MasterTradeTrace | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    forexApi
      .getMasterOrder(orderRef)
      .then((data) => !cancelled && setTrace(data))
      .catch(() => !cancelled && setError("交易追踪加载失败，请稍后重试。"));
    return () => {
      cancelled = true;
    };
  }, [orderRef]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" role="dialog" aria-modal="true" aria-label="主账号交易详情">
      <button className="flex-1 cursor-default" aria-label="关闭交易详情" onClick={onClose} />
      <section className="h-full w-full max-w-xl overflow-y-auto bg-white p-5 shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-surface-border pb-4">
          <div>
            <h3 className="text-base font-semibold text-text-primary">交易追踪详情</h3>
            <p className="mt-1 text-[11px] text-text-muted">执行回写投影与跟单汇总；模拟记录单独标识</p>
          </div>
          <button onClick={onClose} className="rounded-md p-1.5 text-text-muted hover:bg-surface hover:text-text-primary" aria-label="关闭"><i className="ri-close-line text-lg" /></button>
        </div>

        {!trace && !error && <p className="py-10 text-center text-sm text-text-muted">加载中...</p>}
        {error && <div className="mt-4 rounded-lg border border-red-500/20 bg-red-500/5 px-4 py-3 text-sm text-red-500">{error}</div>}
        {trace && (
          <div className="space-y-5 py-4">
            {trace.order.execution_source === "simulation_no_broker" && (
              <p className="rounded-lg bg-amber-500/10 p-3 text-xs text-amber-800">休市模拟：这条主单和关联子单不是 Broker 成交，不能用于自然开盘验收。</p>
            )}
            {trace.order.execution_source === "master_fill_fact" && (
              <p className="rounded-lg bg-blue-500/10 p-3 text-xs text-blue-800">主账号 Broker 成交回报已记录；EA 持仓事件尚未同步，现价、盈亏与平仓状态待核对。</p>
            )}
            {trace.order.source_mtf_run_id && trace.order.agent_id && (
              <Link
                className="inline-flex rounded-lg bg-brand/10 px-3 py-2 text-xs font-medium text-brand"
                to={`/forex/agents/detail?id=${encodeURIComponent(trace.order.agent_id)}&run_id=${encodeURIComponent(trace.order.source_mtf_run_id)}`}
              >查看关联 Master AI 运行</Link>
            )}
            <div className="grid grid-cols-2 gap-3 text-xs">
              {[
                ["Broker Ticket", trace.order.broker_ticket_masked],
                ["品种 / 方向", `${trace.order.symbol} / ${trace.order.action}`],
                ["状态", trace.order.lifecycle_status === "fill_reported" ? "已成交 · 持仓同步中" : trace.order.lifecycle_status === "closed" ? "已平仓" : trace.order.sync_status === "fresh" ? "持仓中" : "持仓中（状态待确认）"],
                ["手数", trace.order.lot ?? "--"],
                ["止损", trace.order.stop_loss ?? "未设置 / 待同步"],
                ["止盈", trace.order.take_profit ?? "未设置 / 待同步"],
                ["最近持仓同步", formatTime(trace.order.last_successful_sync_at)],
                ["盈亏观测时间", formatTime(trace.order.pnl_as_of)],
                ["时间口径", trace.order.time_status === "verified" ? "已核验" : "Broker 时间待核验"],
                ["开仓时间", trace.order.opened_at_utc != null ? formatTime(trace.order.opened_at_utc) : "待核验"],
                ["平仓时间", trace.order.closed_at ? (trace.order.closed_at_utc != null ? formatTime(trace.order.closed_at_utc) : "待核验") : "--"],
                ["信号 ID", trace.order.signal_id || "--"],
                ["主订单事件", trace.order.master_order_event_id || "--"],
              ].map(([label, value]) => (
                <div key={String(label)} className="rounded-lg bg-surface/60 p-3">
                  <p className="text-[10px] text-text-muted">{label}</p>
                  <p className="mt-1 break-all font-mono text-text-primary">{String(value)}</p>
                </div>
              ))}
            </div>

            <div>
              <h4 className="mb-2 text-xs font-semibold text-text-primary">跟单结果汇总</h4>
              <p className="mb-2 text-[10px] text-text-muted">“已接收”只表示执行链已接单，不计入“已成交”。</p>
              <div className="grid grid-cols-4 gap-2">
                {Object.entries(trace.follower_summary).map(([key, value]) => (
                  <div key={key} className="rounded-lg border border-surface-border p-2 text-center">
                    <p className="font-mono text-sm font-semibold text-text-primary">{value}</p>
                    <p className="mt-0.5 text-[9px] text-text-muted">{key === "filled" && trace.order.execution_source === "simulation_no_broker" ? "模拟执行回报" : followerSummaryLabels[key] || key}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h4 className="mb-2 text-xs font-semibold text-text-primary">执行事件时间线</h4>
              <div className="space-y-2">
                {trace.events.map((event, index) => (
                  <div key={String(event.event_ref || index)} className="rounded-lg border border-surface-border px-3 py-2 text-xs">
                    <div className="flex justify-between gap-3"><span className="font-medium text-text-primary">{String(event.event_type || "event")}</span><span className="font-mono text-[10px] text-text-muted">{event.occurred_at_utc != null ? formatTime(event.occurred_at_utc) : "待核验"}</span></div>
                    <p className="mt-1 font-mono text-[10px] text-text-muted">{String(event.event_ref || "--")}</p>
                  </div>
                ))}
                {trace.events.length === 0 && <p className="py-4 text-center text-xs text-text-muted">EA 订单事件尚未同步</p>}
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
